import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import * as antd from 'antd'
import API from 'utils/api'

const RegisterPage = () => {
	const navigate = useNavigate()
	const [loading, setLoading] = useState(false)

	// Form state
	const [academyName, setAcademyName] = useState('')
	const [representativeName, setRepresentativeName] = useState('')
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [phone, setPhone] = useState('')
	const [address, setAddress] = useState('')
	const [agreed, setAgreed] = useState(false)

	const handleSubmit = async () => {
		// Validation
		if (!academyName.trim()) {
			antd.message.error('학원명을 입력해주세요.')
			return
		}
		if (!representativeName.trim()) {
			antd.message.error('대표자명을 입력해주세요.')
			return
		}
		if (!email.trim()) {
			antd.message.error('이메일을 입력해주세요.')
			return
		}
		if (!password || password.length < 6) {
			antd.message.error('비밀번호는 6자 이상이어야 합니다.')
			return
		}
		if (password !== confirmPassword) {
			antd.message.error('비밀번호가 일치하지 않습니다.')
			return
		}
		if (!phone.trim()) {
			antd.message.error('전화번호를 입력해주세요.')
			return
		}
		if (!agreed) {
			antd.message.error('이용약관에 동의해주세요.')
			return
		}

		setLoading(true)
		try {
			const res = await API.post('/public/register/academy', '', {
				academyName: academyName.trim(),
				representativeName: representativeName.trim(),
				email: email.trim(),
				password,
				phone: phone.trim(),
				address: address.trim(),
			})

			if (res.result?.success) {
				antd.message.success('회원가입이 완료되었습니다.')
				navigate('/register/complete')
			} else {
				throw new Error(res.result?.message || '회원가입에 실패했습니다.')
			}
		} catch (err: any) {
			const errorMsg = err.message || '회원가입 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
		} finally {
			setLoading(false)
		}
	}

	return (
		<RegisterContainer>
			<RegisterCard>
				<Logo onClick={() => navigate('/register')}>GoMStem</Logo>
				<Title>학원 가입 신청</Title>

				<FormSection>
					<FormRow>
						<FormLabel>학원명 *</FormLabel>
						<antd.Input
							value={academyName}
							onChange={e => setAcademyName(e.target.value)}
							placeholder="학원명을 입력하세요"
							size="large"
						/>
					</FormRow>

					<FormRow>
						<FormLabel>대표자명 *</FormLabel>
						<antd.Input
							value={representativeName}
							onChange={e => setRepresentativeName(e.target.value)}
							placeholder="대표자 이름을 입력하세요"
							size="large"
						/>
					</FormRow>

					<FormRow>
						<FormLabel>이메일 *</FormLabel>
						<antd.Input
							type="email"
							value={email}
							onChange={e => setEmail(e.target.value)}
							placeholder="이메일을 입력하세요"
							size="large"
						/>
					</FormRow>

					<FormRow>
						<FormLabel>비밀번호 *</FormLabel>
						<antd.Input.Password
							value={password}
							onChange={e => setPassword(e.target.value)}
							placeholder="비밀번호 (6자 이상)"
							size="large"
						/>
					</FormRow>

					<FormRow>
						<FormLabel>비밀번호 확인 *</FormLabel>
						<antd.Input.Password
							value={confirmPassword}
							onChange={e => setConfirmPassword(e.target.value)}
							placeholder="비밀번호를 다시 입력하세요"
							size="large"
						/>
					</FormRow>

					<FormRow>
						<FormLabel>전화번호 *</FormLabel>
						<antd.Input
							value={phone}
							onChange={e => setPhone(e.target.value)}
							placeholder="01012345678"
							size="large"
						/>
					</FormRow>

					<FormRow>
						<FormLabel>주소</FormLabel>
						<antd.Input
							value={address}
							onChange={e => setAddress(e.target.value)}
							placeholder="학원 주소를 입력하세요"
							size="large"
						/>
					</FormRow>

					<AgreementRow>
						<antd.Checkbox checked={agreed} onChange={e => setAgreed(e.target.checked)}>
							이용약관 및 개인정보처리방침에 동의합니다
						</antd.Checkbox>
					</AgreementRow>

					<SubmitButton
						type="primary"
						onClick={handleSubmit}
						loading={loading}
						size="large"
						block
					>
						가입 신청
					</SubmitButton>

					<BackLink onClick={() => navigate('/register')}>
						← 돌아가기
					</BackLink>
				</FormSection>
			</RegisterCard>
		</RegisterContainer>
	)
}

const RegisterContainer = styled.div`
	min-height: 100vh;
	background: linear-gradient(135deg, #800020 0%, #a03040 100%);
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 40px 20px;
`

const RegisterCard = styled.div`
	background: white;
	padding: 48px;
	border-radius: 16px;
	width: 100%;
	max-width: 480px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
`

const Logo = styled.h1`
	font-size: 36px;
	font-weight: 700;
	text-align: center;
	color: #800020;
	margin-bottom: 8px;
	cursor: pointer;
	letter-spacing: 4px;
`

const Title = styled.h2`
	font-size: 20px;
	font-weight: 500;
	text-align: center;
	color: #666;
	margin-bottom: 32px;
`

const FormSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

const FormRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`

const FormLabel = styled.label`
	font-size: 14px;
	font-weight: 500;
	color: #333;
`

const AgreementRow = styled.div`
	margin-top: 8px;
	padding: 12px;
	background: #f8f9fa;
	border-radius: 8px;
`

const SubmitButton = styled(antd.Button)`
	background: #800020;
	border-color: #800020;
	height: 48px;
	font-size: 16px;
	font-weight: 600;
	margin-top: 8px;

	&:hover {
		background: #600018 !important;
		border-color: #600018 !important;
	}
`

const BackLink = styled.a`
	text-align: center;
	color: #666;
	cursor: pointer;
	font-size: 14px;
	margin-top: 16px;

	&:hover {
		color: #800020;
	}
`

export default RegisterPage
